import React from "react";
import Axios from "axios";
import { Avatar, CircularProgress, Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./AIReviews.css";

const PAGE_SIZE = 50;

export default function AIReviews() {
  const [reviews, setReviews] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [industryFilter, setIndustryFilter] = React.useState("all");
  const [sourceFilter, setSourceFilter] = React.useState("all");
  const navigate = useNavigate();

  const fetchReviews = async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await Axios.get(
        `/api/activity/ai-reviews?page=${p}&size=${PAGE_SIZE}`
      );
      setReviews(data.data || []);
      setCount(data.count || 0);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchReviews(page);
  }, [page]);

  const formatDate = (d) => {
    const date = new Date(d);
    return {
      date: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const displayName = (rev) => {
    const u = rev.userId;
    if (!u) return null;
    const full = [u.firstname, u.lastname].filter(Boolean).join(" ");
    return full || u.email || null;
  };

  const industries = [
    ...new Set(reviews.map((r) => r.industry).filter(Boolean)),
  ].sort();
  const sources = [
    ...new Set(reviews.map((r) => r.source).filter(Boolean)),
  ].sort();

  const filtered = reviews.filter((r) => {
    if (industryFilter !== "all" && r.industry !== industryFilter) return false;
    if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="air-loading">
        <CircularProgress style={{ color: "#111827" }} />
      </div>
    );
  }

  return (
    <div className="container admin-user-activity">
      {/* Header */}
      <div className="air-header">
        <div className="air-header-left">
          <p className="air-total">{count}</p>
          <p className="air-total-label">Total AI Reviews</p>
        </div>

        <div className="air-filters">
          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
          >
            <option value="all">All Industries</option>
            {industries.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            {sources.map((src) => (
              <option key={src} value={src}>
                {src}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {filtered.length > 0 ? (
        <>
          <table>
            <thead>
              <tr>
                <th>Submitted By</th>
                <th>Company</th>
                <th>Industry</th>
                <th>Source</th>
                <th>Slides</th>
                <th>Submitted</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rev, i) => {
                const name = displayName(rev);
                const { date, time } = formatDate(rev.createdAt);
                return (
                  <tr key={i}>
                    <td
                      className="pointer"
                      onClick={() =>
                        rev.userId &&
                        navigate(
                          `/admin/user-activity?tab=13&userid=${rev.userId._id}`
                        )
                      }
                    >
                      <div className="air-user-cell">
                        <Avatar
                          src={rev.userId?.avatar || ""}
                          sx={{ width: 34, height: 34, fontSize: 13 }}
                        >
                          {name ? name[0].toUpperCase() : "?"}
                        </Avatar>
                        <div className="air-user-info">
                          <span className="air-user-name">
                            {name || "Guest"}
                          </span>
                          <span className="air-user-email">
                            {rev.userId?.email || rev.email || "—"}
                          </span>
                          {rev.userId?.type && (
                            <span className="air-badge air-badge--type">
                              {rev.userId.type}
                            </span>
                          )}
                          {!rev.userId && (
                            <span className="air-badge air-badge--guest">
                              guest
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>{rev.companyName || "—"}</td>

                    <td>
                      {rev.industry ? (
                        <span className="air-badge air-badge--industry">
                          {rev.industry}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>
                      {rev.source ? (
                        <span className="air-badge air-badge--source">
                          {rev.source}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>
                      <span className="air-slides">
                        {rev.noOfSlides > 0 ? rev.noOfSlides : "—"}
                      </span>
                    </td>

                    <td>
                      <div className="air-date-primary">{date}</div>
                      <div className="air-date-secondary">{time}</div>
                    </td>

                    <td>
                      {rev.fileUrl ? (
                        <a
                          href={rev.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="air-file-link"
                        >
                          View ↗
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {Math.ceil(count / PAGE_SIZE) > 1 && (
            <div className="pagination">
              <Pagination
                count={Math.ceil(count / PAGE_SIZE)}
                page={page}
                onChange={(_, v) => setPage(v)}
              />
            </div>
          )}
        </>
      ) : (
        <div className="air-empty">
          <span className="air-empty-icon">🤖</span>
          <p className="air-empty-text">No AI reviews found</p>
        </div>
      )}
    </div>
  );
}
